import React, { useState, useEffect, useMemo, useCallback } from 'react'
import styled from 'styled-components'
import { Card, Select, Spin, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import AllergenGrid from './AllergenGrid'
import QRCodeGenerator from './QRCodeGenerator'
import { useDispatch, useSelector } from 'react-redux'
import { fetchAllRecipes } from 'src/features/RecipeBuilderSlice'
import { fetchVenueProducts } from 'src/features/VenueProductsSlice'
import allergenEventEmitter from 'src/utils/allergenEventEmitter'
import { AllergenTableDownloadButton } from './ui/DownloadPdfButton'
import { API } from 'src/utils/interceptor'
import { menuBuilderAllergenGridWhyCopy } from 'src/constants/menuBuilderCopy'

const { Option } = Select

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  width: 100%;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
`

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`

const WhyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Why = styled.span`
  color: #666;
  font-size: 14px;
`

const Title = styled.h1`
  font-size: 28px;
  margin: 0;
`

const Controls = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
`

const ScopeSelector = styled(Select)`
  min-width: 200px;
`

const AllergenGridTab = ({
  dishes,
  allergenTypes,
  businessName,
  menus,
  venueId,
}) => {
  const [scope, setScope] = useState('all')
  const [selectedMenu, setSelectedMenu] = useState(null)
  const [menuDetails, setMenuDetails] = useState(null)
  const [loadingMenuDetails, setLoadingMenuDetails] = useState(false)
  const dispatch = useDispatch()
  const venue = useSelector(state => state.venue.venue)
  const recipes = useSelector(state => state.recipes.recipes)
  const venueProducts = useSelector(
    state => state.venueProducts?.products || [],
  )

  // Fetch venue products when component mounts
  useEffect(() => {
    if (venueId && venueProducts.length === 0) {
      dispatch(fetchVenueProducts(venueId))
    }
  }, [venueId, venueProducts.length, dispatch])

  // Listen for allergen update events and refresh data
  useEffect(() => {
    const handleAllergenUpdate = payload => {
      // Local ingredient edits already update recipes/products in Redux.
      if (payload?.skipFullRecipeReload) return

      // Refresh the current view when allergens are updated
      if (scope === 'menu' && selectedMenu) {
        // Refresh venue products for menu view
        if (venueId) {
          dispatch(fetchVenueProducts(venueId))
        }
      } else if (scope === 'all' && venue?._id) {
        // Refresh all recipes data
        dispatch(fetchAllRecipes(venue._id))
      }
    }

    allergenEventEmitter.on('allergen-updated', handleAllergenUpdate)

    return () => {
      allergenEventEmitter.off('allergen-updated', handleAllergenUpdate)
    }
  }, [scope, selectedMenu, dispatch, venue?._id, venueId])

  // Watch for changes in recipes data and trigger refresh
  useEffect(() => {
    if (scope === 'all' && dishes && dishes.length > 0) {
      // console.log('Recipes data changed, triggering refresh...')
      // Data is already fresh from the parent component
    }
  }, [dishes, scope])

  // Watch for recipe changes and refresh allergen data
  // Removed this useEffect as it was causing infinite loops
  // The auto-refresh every 30 seconds should be sufficient

  const handleScopeChange = useCallback(value => {
    setScope(value)
    if (value === 'all') {
      setSelectedMenu(null)
    }
  }, [])

  // Fetch detailed menu data when a menu is selected
  const fetchMenuDetails = useCallback(
    async menuId => {
      if (!menuId) {
        setMenuDetails(null)
        return
      }

      setLoadingMenuDetails(true)
      try {
        // Ensure venue products are loaded before fetching menu details
        if (venueProducts.length === 0) {
          // console.log('Venue products not loaded, fetching them first...')
          await dispatch(fetchVenueProducts(venueId))
        }

        const response = await API.get('api2', `menus/${menuId}/details`)
        // console.log('Menu details API response:', response)
        if (response.success) {
          setMenuDetails(response.menu)
          // console.log('Menu details set:', response.menu)
        } else {
          console.error('Failed to fetch menu details:', response.message)
          setMenuDetails(null)
        }
      } catch (error) {
        console.error('Error fetching menu details:', error)
        setMenuDetails(null)
      } finally {
        setLoadingMenuDetails(false)
      }
    },
    [venueProducts.length, dispatch, venueId],
  )

  const handleMenuChange = useCallback(
    menuId => {
      setSelectedMenu(menuId)
      if (menuId) {
        fetchMenuDetails(menuId)
      } else {
        setMenuDetails(null)
      }
    },
    [fetchMenuDetails],
  )

  const getCurrentData = useMemo(() => {
    if (scope === 'all') {
      console.log(
        'All recipes data - dishes:',
        dishes,
        'allergenTypes:',
        allergenTypes,
        'businessName:',
        businessName,
      )
      return {
        dishes,
        allergenTypes,
        businessName,
        title: `${businessName} – Allergen Information`,
      }
    } else if (
      scope === 'menu' &&
      selectedMenu &&
      menuDetails &&
      recipes.length > 0 &&
      venueProducts.length > 0
    ) {
      // Calculate allergens for menu recipes using the same logic as All Recipes
      // console.log('Calculating allergens for menu:', selectedMenu)
      // console.log('Menu details:', menuDetails)
      // console.log('Available recipes:', recipes.length)
      // console.log('Available venue products:', venueProducts.length)

      // Get all recipes in this menu from the detailed menu data, preserving section information
      const menuRecipes = []
      const sectionHeaders = []

      // Get recipes from all sections
      if (menuDetails.recipesBySection) {
        Object.entries(menuDetails.recipesBySection).forEach(
          ([sectionId, sectionData]) => {
            if (sectionData.recipes && sectionData.recipes.length > 0) {
              // Get the actual section name from the section data
              const sectionName =
                sectionData.section?.name ||
                sectionData.name ||
                `Section ${sectionId}`

              // Add section header
              sectionHeaders.push({
                type: 'section',
                sectionId: sectionId,
                sectionName: sectionName,
                recipeCount: sectionData.recipes.length,
              })

              // Add recipes for this section
              sectionData.recipes.forEach(recipeItem => {
                // Handle both new structure (recipe directly) and old structure (recipe wrapped in object)
                const recipe = recipeItem.recipe || recipeItem
                if (recipe && recipe._id) {
                  menuRecipes.push({
                    ...recipe,
                    sectionId: sectionId,
                    sectionName: sectionName,
                  })
                }
              })
            }
          },
        )
      }

      // Also handle recipes directly in sections array (alternative structure)
      if (
        menuDetails.sections &&
        (!menuDetails.recipesBySection || menuRecipes.length === 0)
      ) {
        menuDetails.sections.forEach(section => {
          if (section.recipes && section.recipes.length > 0) {
            const sectionName = section.name || `Section ${section._id}`

            // Add section header
            sectionHeaders.push({
              type: 'section',
              sectionId: section._id,
              sectionName: sectionName,
              recipeCount: section.recipes.length,
            })

            // Add recipes for this section
            section.recipes.forEach(recipeItem => {
              // Handle both structures
              const recipe = recipeItem.recipe || recipeItem
              if (recipe && recipe._id) {
                menuRecipes.push({
                  ...recipe,
                  sectionId: section._id,
                  sectionName: sectionName,
                })
              }
            })
          }
        })
      }

      // Get unassigned recipes
      if (
        menuDetails.unassignedRecipes &&
        menuDetails.unassignedRecipes.length > 0
      ) {
        // Add unassigned section header
        sectionHeaders.push({
          type: 'section',
          sectionId: 'unassigned',
          sectionName: 'Unassigned',
          recipeCount: menuDetails.unassignedRecipes.length,
        })

        menuDetails.unassignedRecipes.forEach(recipeItem => {
          // Handle both new structure (recipe directly) and old structure (recipe wrapped in object)
          const recipe = recipeItem.recipe || recipeItem
          if (recipe && recipe._id) {
            menuRecipes.push({
              ...recipe,
              sectionId: 'unassigned',
              sectionName: 'Unassigned',
            })
          }
        })
      }

      console.log('Menu recipes processing:', {
        recipesFound: menuRecipes.length,
        sectionsProcessed: sectionHeaders.length,
        hasRecipesBySection: !!menuDetails.recipesBySection,
        hasSectionsArray: !!menuDetails.sections,
        venueProductsAvailable: venueProducts.length,
      })

      // Log first recipe structure for debugging
      if (menuRecipes.length > 0) {
        console.log('First menu recipe structure:', {
          hasIngredients: !!menuRecipes[0].ingredients,
          ingredientsCount: menuRecipes[0].ingredients?.length || 0,
          hasConversions: menuRecipes[0].ingredients?.some(i => i.conversion),
          hasAllergens: !!menuRecipes[0].allergens,
          hasCategory: !!menuRecipes[0].category,
        })
      }

      const recipesWithCalculatedAllergens = menuRecipes

      console.log(
        'Recipes with calculated allergens:',
        recipesWithCalculatedAllergens,
      )

      // Debug specific recipes
      recipesWithCalculatedAllergens.forEach((recipe, index) => {
        console.log(`Recipe ${index + 1} (${recipe.title}):`, {
          allergens: recipe.allergens,
          allergenTraces: recipe.allergenTraces,
          ingredients: recipe.ingredients?.length || 0,
          customIngredients: recipe.customIngredients?.length || 0,
          recipeIngredients: recipe.recipeIngredients?.length || 0,
        })
      })

      // Create combined array with section headers and recipes
      const combinedData = []

      // Group recipes by section
      const recipesBySection = {}
      recipesWithCalculatedAllergens.forEach(recipe => {
        const sectionId = recipe.sectionId || 'unassigned'
        if (!recipesBySection[sectionId]) {
          recipesBySection[sectionId] = []
        }
        recipesBySection[sectionId].push(recipe)
      })

      // Add section headers and their recipes in order
      sectionHeaders.forEach(sectionHeader => {
        // Add section header
        combinedData.push({
          type: 'section',
          name: sectionHeader.sectionName,
          sectionId: sectionHeader.sectionId,
          recipeCount: sectionHeader.recipeCount,
        })

        // Add recipes for this section
        const sectionRecipes = recipesBySection[sectionHeader.sectionId] || []
        sectionRecipes.forEach(recipe => {
          combinedData.push({
            type: 'recipe',
            name: recipe?.title,
            allergens: recipe?.allergens || [],
            allergenTraces: recipe?.allergenTraces || [],
            sectionId: recipe.sectionId,
            sectionName: recipe.sectionName,
          })
        })
      })

      // console.log('Combined data with sections:', combinedData)
      return {
        dishes: combinedData,
        allergenTypes: allergenTypes || [],
        businessName,
        title: `${businessName} – ${menuDetails.name} – Allergen Information`,
      }
    } else if (scope === 'menu' && !selectedMenu) {
      console.log('Menu scope selected but no menu selected')
    } else if (
      scope === 'menu' &&
      (recipes.length === 0 || venueProducts.length === 0)
    ) {
      console.log(
        'Menu scope selected but missing data - recipes:',
        recipes.length,
        'venueProducts:',
        venueProducts.length,
      )
    }
    return null
  }, [
    scope,
    selectedMenu,
    menuDetails,
    dishes,
    allergenTypes,
    businessName,
    menus,
    recipes,
    venueProducts,
  ])

  const currentData = getCurrentData

  return (
    <Container>
      <Header>
        <TitleBlock>
          <Title>Allergen Grids</Title>
          <WhyRow>
            <Why data-testid='allergen-grids-why-subtitle'>
              {menuBuilderAllergenGridWhyCopy.subtitle}
            </Why>
            <Tooltip title={menuBuilderAllergenGridWhyCopy.tooltip}>
              <InfoCircleOutlined
                style={{ color: '#1890ff' }}
                data-testid='allergen-grids-why-info'
              />
            </Tooltip>
          </WhyRow>
        </TitleBlock>
        <Controls>
          <ScopeSelector
            value={scope}
            onChange={handleScopeChange}
            placeholder='Select a menu...'
          >
            <Option value='all'>All Recipes</Option>
            <Option value='menu'>Specific Menu</Option>
          </ScopeSelector>

          {scope === 'menu' && (
            <Select
              value={selectedMenu}
              onChange={handleMenuChange}
              placeholder='Select a menu...'
              style={{ minWidth: 200 }}
            >
              {menus
                ?.filter(menu => menu.status === 'active')
                .map(menu => (
                  <Option key={menu._id} value={menu._id}>
                    {menu.name}
                  </Option>
                ))}
            </Select>
          )}

          <ButtonGroup>
            {scope === 'menu' && selectedMenu && currentData && (
              <QRCodeGenerator
                menuId={selectedMenu}
                menuName={currentData.title?.split(' – ')[1] || 'Menu'}
              />
            )}

            <AllergenTableDownloadButton
              isAllRecipes={scope === 'all'}
              allergenData={currentData}
            />
          </ButtonGroup>
        </Controls>
      </Header>

      {loadingMenuDetails ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size='large' />
            <div style={{ marginTop: '16px' }}>Loading menu details...</div>
          </div>
        </Card>
      ) : !currentData ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '16px', color: '#666' }}>
              {scope === 'menu' && !selectedMenu
                ? 'Please select a menu to view allergen information'
                : 'Select a scope to view allergen information'}
            </div>
          </div>
        </Card>
      ) : currentData ? (
        <AllergenGrid
          dishes={currentData.dishes}
          businessName={currentData.businessName}
          isAllRecipes={true}
        />
      ) : (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>Select a scope to view allergen information</div>
          </div>
        </Card>
      )}
    </Container>
  )
}

export default AllergenGridTab
